/**
 * Metrics (CFO plan 4.4): what gets counted — business measures (sq ft per hour, paint used, fuel per 100 km) and
 * personal counters (a tap on a Home tile). Pure — no I/O.
 *
 * A definition (business `metrics/{id}` = opsMetrics, owners write / everyone reads; personal
 * `personalMetrics/{id}` = opsUsers/{uid}/metrics, private):
 *   { name, unit, kind, agg, source: 'manual' | 'auto', auto: { key, tag?, routineId?, unit? } | null,
 *     attachTo: [task|project|person|equipment|jobType|tag|org], goal: { value, period, direction } | null,
 *     derived: { num, den, scale, unit? } | null, tile?, icon? }
 *   `derived` refs are an auto key ('task.qty') or another metric's id; `tile` = a personal counter shown on Home.
 * A value (business `metricValues/{id}` = opsMetricValues — about `uid`: yours, or everyone's for an owner; personal
 * `personalMetricValues/{id}` = opsUsers/{uid}/metricValues):
 *   { metricId, value, at, date, source: tap|task|project|auto|routine, target: { kind, id } | null, taskId?,
 *     projectId?, uid, note? }
 *
 * Auto values are computed on read from what the app already has (sessions, tasks, the books' receipts and jobs,
 * routine runs) and never stored. Capture order: auto first, then the quoted quantity, then ask on completion.
 */
import { localDateStr } from './time.js';
import { addDaysStr } from './phonestats.js';
import { canonTag } from './tags.js';

export const METRIC_KINDS = ['number', 'money', 'duration', 'distance', 'volume', 'yesno', 'rating', 'count'];
export const METRIC_KIND_LABELS = { number: 'Number', money: 'Money', duration: 'Hours', distance: 'Distance', volume: 'Volume', yesno: 'Yes / no', rating: 'Rating 1–5', count: 'Count' };
export const METRIC_AGGS = ['sum', 'avg', 'min', 'max', 'latest'];
export const METRIC_AGG_LABELS = { sum: 'Total', avg: 'Average', min: 'Lowest', max: 'Highest', latest: 'Latest' };
export const METRIC_ATTACH = ['task', 'project', 'person', 'equipment', 'jobType', 'tag', 'org'];
export const METRIC_PERIODS = ['day', 'week', 'month'];
export const VALUE_SOURCES = ['tap', 'task', 'project', 'auto', 'routine'];
export const ROLLUP_BY = ['task', 'project', 'jobType', 'tag', 'org', 'person'];

/** The auto registry: what can be counted without anyone typing it. `routine` stands for `routine.<stepId>`. */
export const AUTO_METRICS = [
  { key: 'clock.hours', label: 'Hours on the clock', unit: 'h', kind: 'duration' },
  { key: 'task.hours', label: 'Hours on tasks', unit: 'h', kind: 'duration' },
  { key: 'trip.km', label: 'Km driven', unit: 'km', kind: 'distance' },
  { key: 'task.qty', label: 'Quantity done (from the quote)', unit: null, kind: 'number' },
  { key: 'books.fuel.litres', label: 'Fuel bought (litres)', unit: 'L', kind: 'volume' },
  { key: 'books.fuel.$', label: 'Fuel bought ($)', unit: '$', kind: 'money' },
  { key: 'books.$', label: 'Spent in the books (by tag)', unit: '$', kind: 'money' },
  { key: 'routine', label: 'A routine step’s number', unit: null, kind: 'number' },
];
const AUTO_BY = new Map(AUTO_METRICS.map((a) => [a.key, a]));
/** The registry row for a key ('routine.s2' → the routine row). */
export const autoMeta = (key) => (String(key || '').startsWith('routine.') ? AUTO_BY.get('routine') : AUTO_BY.get(key)) || null;
export const isAutoKey = (key) => !!autoMeta(key) && key !== 'routine';

/** Definitions the starter packs bring (templates.js STARTER_PACKS `metrics`), by their fixed id. */
export const STARTER_METRICS = Object.freeze({
  'sqft-per-hour': { name: 'Sq ft per hour', unit: 'sq ft/h', kind: 'number', agg: 'avg', source: 'auto', attachTo: ['task', 'project', 'person', 'jobType'], derived: { num: 'task.qty', den: 'task.hours', scale: 1, unit: 'sq ft' } },
  'paint-litres': { name: 'Paint used', unit: 'L', kind: 'volume', agg: 'sum', source: 'manual', attachTo: ['task', 'project'] },
});

const H = 3600000;
const str = (v) => String(v ?? '').trim();
const num = (v) => (v === null || v === undefined || v === '' || typeof v === 'boolean' ? null : Number.isFinite(Number(v)) ? Number(v) : null);
const round = (x, d = 2) => (x == null ? null : Math.round(x * 10 ** d) / 10 ** d);
const unitKey = (u) => str(u).toLowerCase().replace(/[.\s]+/g, '');

/** A clean definition (unknown fields dropped). */
export function makeMetric(o = {}) {
  const goalV = num(o.goal?.value);
  const auto = o.source === 'auto' && o.auto && str(o.auto.key) ? {
    key: str(o.auto.key),
    ...(str(o.auto.tag) ? { tag: canonTag(o.auto.tag) } : {}),
    ...(str(o.auto.routineId) ? { routineId: str(o.auto.routineId) } : {}),
    ...(str(o.auto.unit) ? { unit: str(o.auto.unit) } : {}),
  } : null;
  const d = o.derived;
  const derived = d && str(d.num) && str(d.den) ? { num: str(d.num), den: str(d.den), scale: num(d.scale) || 1, ...(str(d.unit) ? { unit: str(d.unit) } : {}) } : null;
  return {
    ...(o.id ? { id: o.id } : {}),
    name: str(o.name),
    unit: str(o.unit) || (auto ? autoMeta(auto.key)?.unit || null : null),
    kind: METRIC_KINDS.includes(o.kind) ? o.kind : 'count',
    agg: METRIC_AGGS.includes(o.agg) ? o.agg : 'sum',
    source: auto || derived ? 'auto' : 'manual',
    auto,
    attachTo: [...new Set((Array.isArray(o.attachTo) ? o.attachTo : []).filter((a) => METRIC_ATTACH.includes(a)))],
    goal: goalV != null ? { value: goalV, period: METRIC_PERIODS.includes(o.goal.period) ? o.goal.period : 'day', direction: o.goal.direction === 'down' ? 'down' : 'up' } : null,
    derived,
    ...(o.tile ? { tile: true } : {}),
    ...(str(o.icon) ? { icon: str(o.icon) } : {}),
    ...(o.starterPack ? { starterPack: o.starterPack } : {}),
    ...(o.createdAt ? { createdAt: o.createdAt } : {}),
    ...(o.updatedAt ? { updatedAt: o.updatedAt } : {}),
  };
}

/** A value as stored: numbers only (yes = 1, no = 0). */
export function metricNumber(def, v) {
  if (def?.kind === 'yesno' || typeof v === 'boolean') return v === true || v === 1 || v === '1' || /^(y|yes|true)$/i.test(String(v)) ? 1 : 0;
  const n = num(typeof v === 'string' ? v.replace(',', '.') : v);
  if (n == null) return null;
  if (def?.kind === 'rating') return Math.min(5, Math.max(1, Math.round(n)));
  return n;
}

/** Which routine capture input asks for it (CAPTURE_TYPES keys) — the completion sheet reuses those inputs. */
export function metricCaptureType(def) {
  const type = def?.kind === 'yesno' ? 'yesno' : def?.kind === 'rating' ? 'rating' : 'number';
  return { type, unit: def?.unit || null, label: def?.name || null };
}

// ── Auto values (computed on read) ────────────────────────────────────────────────────────────────────────────

const row = (o) => ({ source: 'auto', target: null, taskId: null, projectId: null, uid: null, ...o, date: o.date || (o.at ? localDateStr(o.at) : null) });
const taskUid = (t, me) => t.assignee || t.ownerUid || me || null;
export const isFuelLine = (l) => (l.tags || []).some((t) => canonTag(t) === 'fuel') || /\b(fuel|gas(oline)?|diesel)\b/i.test(`${l.category || ''} ${l.description || ''}`);
const equipOf = (tags) => { const t = (tags || []).map(canonTag).find((x) => x.startsWith('equip-')); return t ? t.slice(6) : null; };

/** Live receipt lines from the books' field receipts (opsReceipts pages; void pages skipped), with the ops project. */
export function receiptLines(receipts = [], projects = []) {
  const opsOf = new Map(projects.filter((p) => p.bookProjectId).map((p) => [p.bookProjectId, p.id]));
  const out = [];
  for (const r of receipts) {
    for (const page of Object.values(r?.pages || {})) {
      if (!page || page.status === 'void') continue;
      const at = (typeof page.date === 'number' ? page.date : null) || page.postedAt || r.updatedAt || null;
      const date = typeof page.date === 'string' ? page.date : at ? localDateStr(at) : null;   // the books send ms
      for (const l of page.lines || []) out.push({ ...l, at, date, uid: r.purchasedByUid || null, projectId: l.projectId ? opsOf.get(l.projectId) || null : null });
    }
  }
  return out;
}

/**
 * Rows for one auto key. ctx = { sessions, tasks, projects, receipts (opsReceipts), jobs (opsJobs), runs (routine runs),
 * trips (described off-clock trips: { start, km, onClock }), me, now }. opts = the def's `auto` ({ tag, routineId, unit }).
 */
export function autoValues(key, ctx = {}, opts = {}) {
  const { sessions = [], tasks = [], projects = [], receipts = [], jobs = [], runs = [], trips = [], me = null, now = Date.now() } = ctx;
  const k = str(key);
  if (k === 'clock.hours') {
    return sessions.filter((s) => s && s.startedAt && !s.overlay && s.type !== 'travel' && !s.deletedAt).map((s) => {
      const ms = s.endedAt ? (s.duration || Math.max(0, s.endedAt - s.startedAt)) : Math.max(0, now - s.startedAt);
      const uid = s.ownerUid || me;
      return row({ value: round(ms / H), at: s.startedAt, uid, projectId: s.projectId || null, target: { kind: 'person', id: uid } });
    });
  }
  if (k === 'task.hours') {
    const out = [];
    for (const t of tasks) {
      if (!t || t.deletedAt || t.bucket) continue;
      for (const e of t.timeEntries || []) {
        if (!(e?.duration > 0)) continue;
        const at = e.createdAt || (e.date ? new Date(`${e.date}T${e.startTime || '12:00'}:00`).getTime() : null);
        out.push(row({ value: round(e.duration / H, 4), at, date: e.date || null, uid: e.uid || taskUid(t, me), taskId: t.id, projectId: t.projectId || null, target: { kind: 'task', id: t.id } }));
      }
    }
    return out;
  }
  if (k === 'trip.km') {
    const out = sessions.filter((s) => s && s.type === 'travel' && s.distanceM > 0 && s.startedAt).map((s) => {
      const uid = s.ownerUid || me;
      return row({ value: round(s.distanceM / 1000, 1), at: s.startedAt, uid, projectId: s.projectId || null, target: { kind: 'person', id: uid } });
    });
    for (const t of trips) if (t && t.onClock === false && t.km > 0) out.push(row({ value: t.km, at: t.start, uid: me, target: { kind: 'person', id: me } }));
    return out;
  }
  if (k === 'task.qty') {
    const want = unitKey(opts.unit);
    return tasks.filter((t) => t && !t.deletedAt && t.status === 'done' && num(t.qty) > 0 && (!want || unitKey(t.unit) === want)).map((t) => row({
      value: Number(t.qty), unit: t.unit || null, at: t.completedAt || t.updatedAt || null, uid: taskUid(t, me), taskId: t.id, projectId: t.projectId || null, target: { kind: 'task', id: t.id },
    }));
  }
  if (k === 'books.fuel.litres' || k === 'books.fuel.$') {
    const litres = k === 'books.fuel.litres';
    return receiptLines(receipts, projects).filter((l) => isFuelLine(l) && (litres ? num(l.qty) > 0 : num(l.amount) > 0)).map((l) => {
      const equip = equipOf(l.tags);
      return row({ value: litres ? Number(l.qty) : round(Number(l.amount)), at: l.at, date: l.date, uid: l.uid, taskId: l.taskId || null, projectId: l.projectId, target: equip ? { kind: 'equipment', id: equip } : l.projectId ? { kind: 'project', id: l.projectId } : null });
    });
  }
  if (k === 'books.$') {
    // The job's costs by task (labour, materials, subs, other), each task's tags; with no tag, a job's cost not on a
    // task too. With a tag, receipt lines tagged with it that sit on no task are added.
    const tag = opts.tag ? canonTag(opts.tag) : null;
    const byId = new Map(tasks.map((t) => [t.id, t]));
    const opsOf = new Map(projects.filter((p) => p.bookProjectId).map((p) => [p.bookProjectId, p]));
    const out = [];
    for (const j of jobs) {
      const proj = opsOf.get(j.projectId || j.id) || (j.opsProjectId ? projects.find((p) => p.id === j.opsProjectId) : null);
      const at = j.updatedAt || j.publishedAt || now;
      let onTasks = 0;
      for (const [taskId, c] of Object.entries(j.byTask || {})) {
        const cost = Object.values(c || {}).reduce((a, v) => a + (Number(v) || 0), 0);
        onTasks += cost;
        const t = byId.get(taskId);
        if (!cost || (tag && !(t?.tags || []).map(canonTag).includes(tag))) continue;
        out.push(row({ value: round(cost), at: t?.completedAt || at, uid: t ? taskUid(t, me) : null, taskId, projectId: proj?.id || t?.projectId || null, target: { kind: 'task', id: taskId } }));
      }
      const rest = round((Number(j.costToDate) || 0) - onTasks);
      if (!tag && rest > 0 && proj) out.push(row({ value: rest, at, projectId: proj.id, target: { kind: 'project', id: proj.id } }));
    }
    if (tag) {
      for (const l of receiptLines(receipts, projects)) {
        if (l.taskId || !(l.tags || []).map(canonTag).includes(tag) || !(num(l.amount) > 0)) continue;
        out.push(row({ value: round(Number(l.amount)), at: l.at, date: l.date, uid: l.uid, projectId: l.projectId, target: { kind: 'tag', id: tag } }));
      }
    }
    return out;
  }
  if (k.startsWith('routine.')) {
    const stepId = k.slice(8);
    const out = [];
    for (const r of runs) {
      if (!r || (opts.routineId && r.routineId !== opts.routineId)) continue;
      for (const s of r.steps || []) {
        if (s.stepId !== stepId) continue;
        const v = typeof s.value === 'boolean' ? (s.value ? 1 : 0) : num(s.value);
        if (v == null) continue;
        const uid = r.uid || me;
        out.push(row({ value: v, source: 'routine', at: s.doneAt || r.finishedAt || r.startedAt || null, date: r.date || null, uid, target: { kind: 'person', id: uid } }));
      }
    }
    return out;
  }
  return [];
}

/** A definition's rows: computed when it's auto, else the stored values for it. Derived defs have none (derive). */
export function valuesFor(def, { values = [], ctx = {} } = {}) {
  if (!def || def.derived) return [];
  if (def.source === 'auto' && def.auto?.key) return autoValues(def.auto.key, ctx, def.auto).map((v) => ({ ...v, metricId: def.id }));
  return values.filter((v) => v && v.metricId === def.id);
}

/** A ref (auto key or metric id) → rows. */
function refRows(ref, { defs = [], values = [], ctx = {}, unit = null }) {
  if (isAutoKey(ref)) return autoValues(ref, ctx, ref === 'task.qty' && unit ? { unit } : {});
  const d = defs.find((x) => x.id === ref);
  return d ? valuesFor(d, { values, ctx }) : [];
}

// ── Grouping, roll-ups, derived ────────────────────────────────────────────────────────────────────────────────

/** The period bucket a date falls in: the day, its week's Sunday (or weekStart), the 1st of its month. */
export function periodKey(date, period = 'day', weekStart = 0) {
  if (!date) return null;
  if (period === 'month') return `${date.slice(0, 8)}01`;
  if (period === 'week') { const dow = new Date(`${date}T12:00:00`).getDay(); return addDaysStr(date, -((dow - weekStart + 7) % 7)); }
  return date;
}

/** One number from rows by the def's agg. */
export function aggregate(rows, agg = 'sum') {
  const vals = rows.map((r) => num(r.value)).filter((v) => v != null);
  if (!vals.length) return null;
  if (agg === 'avg') return round(vals.reduce((a, b) => a + b, 0) / vals.length);
  if (agg === 'min') return Math.min(...vals);
  if (agg === 'max') return Math.max(...vals);
  if (agg === 'latest') return num([...rows].filter((r) => num(r.value) != null).sort((a, b) => (b.at || 0) - (a.at || 0))[0].value);
  return round(vals.reduce((a, b) => a + b, 0));
}

/** The groups a row belongs to for `by` (a row with several tags is in each tag's group). ctx: tasks, projects. */
function groupsOf(r, by, { taskById, projectById }) {
  if (by === 'org') return ['org'];
  if (by === 'person') return r.uid ? [r.uid] : [];
  if (by === 'task') return r.taskId ? [r.taskId] : [];
  const t = r.taskId ? taskById.get(r.taskId) : null;
  const pid = r.projectId || t?.projectId || null;
  if (by === 'project') return pid ? [pid] : [];
  if (by === 'jobType') { const jt = pid ? projectById.get(pid)?.jobType : null; return jt ? [jt] : []; }
  if (by === 'tag') {
    const tags = new Set([...(t?.tags || []), ...((pid && projectById.get(pid)?.tags) || []), ...(r.target?.kind === 'tag' ? [r.target.id] : [])].map(canonTag).filter(Boolean));
    return [...tags];
  }
  return [];
}
const indexes = (ctx = {}) => ({ taskById: new Map((ctx.tasks || []).map((t) => [t.id, t])), projectById: new Map((ctx.projects || []).map((p) => [p.id, p])) });

/**
 * Rows grouped by task | project | jobType | tag | org | person, and by period when given:
 *   [{ key, period, value, n }] — value by `agg`. from / to (dates) and projectId (one job) bound it.
 */
export function rollup(rows, { by = 'org', period = null, agg = 'sum', from = null, to = null, ctx = {}, weekStart = 0, projectId = null } = {}) {
  const ix = indexes(ctx);
  const groups = new Map();
  for (const r of rows || []) {
    if ((from && r.date && r.date < from) || (to && r.date && r.date > to)) continue;
    if (projectId && (r.projectId || ix.taskById.get(r.taskId)?.projectId || null) !== projectId) continue;   // one job's rows
    const p = period ? periodKey(r.date, period, weekStart) : null;
    if (period && !p) continue;
    for (const g of groupsOf(r, by, ix)) {
      const k = `${g}|${p ?? ''}`;
      if (!groups.has(k)) groups.set(k, { key: g, period: p, rows: [] });
      groups.get(k).rows.push(r);
    }
  }
  return [...groups.values()].map((g) => ({ key: g.key, period: g.period, value: aggregate(g.rows, agg), n: g.rows.length }))
    .sort((a, b) => String(a.period || '').localeCompare(String(b.period || '')) || String(a.key).localeCompare(String(b.key)));
}

/**
 * A derived metric (num ÷ den × scale) per group: sq ft / h = quantity ÷ hours on those tasks; $ / sq ft = job cost ÷
 * quantity; L / 100 km = fuel ÷ km × 100. When both sides are per task, only tasks on both sides count (hours on a
 * task with no quantity don't dilute the rate). → [{ key, period, num, den, value }].
 */
export function derive(def, { defs = [], values = [], ctx = {}, by = 'org', period = null, from = null, to = null, weekStart = 0, projectId = null } = {}) {
  const d = def?.derived;
  if (!d) return [];
  let numRows = refRows(d.num, { defs, values, ctx, unit: d.unit });
  let denRows = refRows(d.den, { defs, values, ctx, unit: d.unit });
  const tasksOf = (rows) => (rows.length && rows.every((r) => r.taskId) ? new Set(rows.map((r) => r.taskId)) : null);
  const nt = tasksOf(numRows); const dt = tasksOf(denRows);
  if (nt && dt) { numRows = numRows.filter((r) => dt.has(r.taskId)); denRows = denRows.filter((r) => nt.has(r.taskId)); }
  const opts = { by, period, agg: 'sum', from, to, ctx, weekStart, projectId };
  const n = rollup(numRows, opts); const m = rollup(denRows, opts);
  const den = new Map(m.map((x) => [`${x.key}|${x.period ?? ''}`, x.value]));
  return n.map((x) => {
    const dv = den.get(`${x.key}|${x.period ?? ''}`) || 0;
    return { key: x.key, period: x.period, num: x.value, den: dv, value: dv > 0 ? round((x.value / dv) * (d.scale || 1)) : null };
  }).filter((x) => x.value != null);
}

/** Any def → [{ key, period, value }] by group / period (derived or plain); projectId = one job's figures only. */
export function metricReport(def, { defs = [], values = [], ctx = {}, by = 'org', period = null, from = null, to = null, weekStart = 0, projectId = null } = {}) {
  if (def?.derived) return derive(def, { defs, values, ctx, by, period, from, to, weekStart, projectId });
  return rollup(valuesFor(def, { values, ctx }), { by, period, agg: def?.agg || 'sum', from, to, ctx, weekStart, projectId });
}

/** "Sam 85 sq ft/h vs company 70": each person's figure beside the company's over the window. */
export function metricRates(def, { defs = [], values = [], ctx = {}, from = null, to = null } = {}) {
  const people = metricReport(def, { defs, values, ctx, by: 'person', from, to });
  const org = metricReport(def, { defs, values, ctx, by: 'org', from, to })[0]?.value ?? null;
  return { org, people: people.map((p) => ({ uid: p.key, value: p.value, vsOrg: org ? round(p.value / org - 1, 3) : null })).sort((a, b) => (b.value ?? 0) - (a.value ?? 0)) };
}

/** Buckets from `from` to `to` (inclusive) for a chart, each with the def's value (null when nothing). */
export function metricSeries(def, { defs = [], values = [], ctx = {}, from, to, period = 'day', by = 'org', key = 'org', weekStart = 0 } = {}) {
  const rows = metricReport(def, { defs, values, ctx, by, period, from, to, weekStart }).filter((r) => r.key === key);
  const at = new Map(rows.map((r) => [r.period, r.value]));
  const out = [];
  const seen = new Set();
  for (let d = from, i = 0; d && d <= to && i < 800; d = addDaysStr(d, 1), i++) {
    const p = periodKey(d, period, weekStart);
    if (seen.has(p)) continue;
    seen.add(p);
    out.push({ period: p, value: at.get(p) ?? null });
  }
  return out;
}

// ── Personal counters: totals, time of day, streaks, goals, patterns ─────────────────────────────────────────

const sumOn = (rows, pred) => round(rows.filter(pred).reduce((a, r) => a + (num(r.value) || 0), 0));

/** Today / this week / this month for a counter (by its agg; sum for counts). */
export function periodTotals(def, rows, { now = Date.now(), weekStart = 0 } = {}) {
  const today = localDateStr(now);
  const wk = periodKey(today, 'week', weekStart);
  const mo = periodKey(today, 'month');
  const agg = def?.agg || 'sum';
  const pick = (pred) => aggregate(rows.filter(pred), agg);
  return { day: pick((r) => r.date === today), week: pick((r) => r.date >= wk && r.date <= today), month: pick((r) => r.date >= mo && r.date <= today) };
}

/** 24 hourly buckets (local) — when in the day it happens. */
export function timeOfDay(rows) {
  const out = Array.from({ length: 24 }, (_, h) => ({ hour: h, value: 0, n: 0 }));
  for (const r of rows) { if (!r.at) continue; const h = new Date(r.at).getHours(); out[h].value = round(out[h].value + (num(r.value) || 0)); out[h].n += 1; }
  return out;
}

/** Is a day's total on target? up: ≥ goal (or > 0 with no goal); down: ≤ goal (a day-goal only; else any total counts). */
const dayOk = (def, total) => {
  const g = def?.goal;
  if (g && g.period === 'day') return g.direction === 'down' ? total <= g.value : total >= g.value;
  if (g?.direction === 'down') return total === 0;   // less is better, no day goal: a day without any
  return total > 0;
};

/**
 * Days in a row on target, ending today (today still open doesn't break an up-streak) — the routineStats rule: a miss
 * breaks it.
 * Counted from the first day with a value. → { current, best }.
 */
export function counterStreak(def, rows, { now = Date.now() } = {}) {
  const today = localDateStr(now);
  const byDate = new Map();
  for (const r of rows) if (r.date) byDate.set(r.date, (byDate.get(r.date) || 0) + (num(r.value) || 0));
  const first = [...byDate.keys()].sort()[0];
  if (!first) return { current: 0, best: 0 };
  let best = 0; let run = 0;
  for (let d = first, i = 0; d <= today && i < 1500; d = addDaysStr(d, 1), i++) {
    const ok = dayOk(def, byDate.get(d) || 0);
    if (d === today && !ok && def?.goal?.direction !== 'down') break;   // today isn't over
    if (ok) { run += 1; best = Math.max(best, run); } else run = 0;
  }
  return { current: run, best };
}

/** Where the goal stands this period: { period, target, direction, value, pct, met }. null with no goal. */
export function goalProgress(def, rows, { now = Date.now(), weekStart = 0 } = {}) {
  const g = def?.goal;
  if (!g) return null;
  const t = periodTotals(def, rows, { now, weekStart })[g.period] ?? 0;
  return { period: g.period, target: g.value, direction: g.direction, value: t, pct: g.value ? Math.round((t / g.value) * 100) : null, met: g.direction === 'down' ? t <= g.value : t >= g.value };
}

/**
 * Did adding `value` just now cross the goal? 'reached' (up: from under to at/over), 'over' (down: from at/under to
 * over), else null. rows = before the new value.
 */
export function goalCrossed(def, rows, value, { now = Date.now(), weekStart = 0 } = {}) {
  const g = def?.goal;
  if (!g) return null;
  const before = periodTotals(def, rows, { now, weekStart })[g.period] ?? 0;
  const today = localDateStr(now);
  const after = periodTotals(def, [...rows, { value, date: today, at: now }], { now, weekStart })[g.period] ?? 0;
  if (g.direction === 'down') return before <= g.value && after > g.value ? 'over' : null;
  return before < g.value && after >= g.value ? 'reached' : null;
}

/**
 * A counter beside the person's own clock: per day the count, hours clocked and whether the day started late (first
 * clock-in more than 30 min after their usual first clock-in — the median over the window). Private and simple:
 * averages on late vs on-time days and on long vs short days (clocked ≥ the median day or not).
 */
export function counterPatterns(rows, { sessions = [], uid = null, from, to, lateSlackMin = 30 } = {}) {
  const mine = sessions.filter((s) => s && s.startedAt && !s.overlay && s.type !== 'travel' && (!uid || !s.ownerUid || s.ownerUid === uid));
  const firstIn = new Map(); const clocked = new Map();
  for (const s of mine) {
    const d = localDateStr(s.startedAt);
    if (d < from || d > to) continue;
    const t = new Date(s.startedAt); const min = t.getHours() * 60 + t.getMinutes();
    if (!firstIn.has(d) || min < firstIn.get(d)) firstIn.set(d, min);
    clocked.set(d, (clocked.get(d) || 0) + (s.endedAt ? (s.duration || Math.max(0, s.endedAt - s.startedAt)) : 0) / H);
  }
  const med = (a) => { const x = [...a].sort((p, q) => p - q); const m = Math.floor(x.length / 2); return x.length ? (x.length % 2 ? x[m] : (x[m - 1] + x[m]) / 2) : null; };
  const usual = med([...firstIn.values()]);
  const midH = med([...clocked.values()].filter((h) => h > 0));
  const days = [];
  for (let d = from, i = 0; d <= to && i < 800; d = addDaysStr(d, 1), i++) {
    const h = clocked.get(d) || 0;
    days.push({ date: d, count: sumOn(rows, (r) => r.date === d), clockedH: round(h, 1), late: firstIn.has(d) && usual != null ? firstIn.get(d) > usual + lateSlackMin : null });
  }
  const avg = (list) => (list.length ? round(list.reduce((a, x) => a + x.count, 0) / list.length, 1) : null);
  const worked = days.filter((d) => d.clockedH > 0);
  return {
    days,
    usualStartMin: usual,
    lateDays: avg(worked.filter((d) => d.late)), onTimeDays: avg(worked.filter((d) => d.late === false)),
    longDays: midH == null ? null : avg(worked.filter((d) => d.clockedH >= midH)), shortDays: midH == null ? null : avg(worked.filter((d) => d.clockedH < midH)),
    offDays: avg(days.filter((d) => !(d.clockedH > 0))),
    medianHours: round(midH, 1),
  };
}

/** The metrics a job shows: its type's, its tasks' template metrics, and any defined for jobs (attachTo project). */
export function projectMetricDefs({ project, type = null, tasks = [], defs = [] }) {
  const ids = new Set([...(Array.isArray(type?.metrics) ? type.metrics : [])]);
  for (const t of tasks) if (t?.projectId === project?.id) for (const m of t.metrics || []) if (m?.metricId) ids.add(m.metricId);
  return defs.filter((d) => ids.has(d.id) || (d.attachTo || []).includes('project'));
}

// ── Capture on completion + the trust meter ───────────────────────────────────────────────────────────────────

/**
 * What to ask when a task is done: its template metrics with source 'ask' (task.metrics), whose definition exists,
 * that have no value on this task yet and that the quote doesn't already answer (a quoted quantity in the metric's
 * unit). → definitions, in the task's order.
 */
export function askOnComplete(task, defs = [], values = []) {
  const byId = new Map(defs.map((d) => [d.id, d]));
  const have = new Set(values.filter((v) => v?.taskId === task?.id).map((v) => v.metricId));
  const quoted = num(task?.qty) > 0 ? unitKey(task.unit) : null;
  const out = [];
  for (const m of Array.isArray(task?.metrics) ? task.metrics : []) {
    if (!m || m.source !== 'ask') continue;
    const d = byId.get(m.metricId);
    if (!d || have.has(d.id) || out.includes(d)) continue;
    if (quoted && d.unit && unitKey(d.unit) === quoted) continue;
    out.push(d);
  }
  return out;
}

/** Done tasks still missing an asked value (the trust meter): [{ taskId, title, projectId, metricId, name }]. */
export function missingAsked({ tasks = [], defs = [], values = [], projectId = null } = {}) {
  const out = [];
  for (const t of tasks) {
    if (!t || t.status !== 'done' || t.deletedAt || (projectId && t.projectId !== projectId)) continue;
    for (const d of askOnComplete(t, defs, values)) out.push({ taskId: t.id, title: t.title || 'Task', projectId: t.projectId || null, metricId: d.id, name: d.name });
  }
  return out;
}

/** A value as a short string with its unit: "12.5 L", "$40", "1.2 h", "Yes". */
export function formatMetric(def, v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (def?.kind === 'yesno') return n >= 1 ? 'Yes' : 'No';
  if (def?.kind === 'money' || def?.unit === '$') return `$${n.toLocaleString('en-CA', { maximumFractionDigits: n >= 100 ? 0 : 2 })}`;
  const s = Math.abs(n) >= 100 ? Math.round(n).toLocaleString('en-CA') : String(round(n, Math.abs(n) >= 10 ? 1 : 2));
  return def?.unit ? `${s} ${def.unit}` : s;
}
